import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { useVerifyPhonePopup } from 'features/account/verify-phone/useVerifyPhonePopup';
import { useNotification } from 'providers/NotificationProvider';
import { ApiResponse } from 'services/apiResponse';
import { notificationService } from 'services/notification/notificationService';
import { ChannelDto, ChannelState, ChannelType } from 'services/notification/notificationService.dto';

export const useNotificationChannels = () => {
  const { t } = useTranslation();
  const { showVerifyPhonePopup } = useVerifyPhonePopup();
  const { showNotification } = useNotification();

  const [response, setResponse] = useState<ApiResponse<ChannelDto[]>>({ loading: true });

  const fetchChannels = async () => {
    setResponse(prev => ({ ...prev, loading: true, error: undefined }));
    try {
      const { data } = await notificationService.fetchChannels();
      const filtered = data.filter((channel: ChannelDto) => channel.type !== 'PUSH');
      setResponse({ loading: false, content: filtered });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setResponse({ loading: false, content: [], error });
      } else {
        throw error;
      }
    }
  };

  const updateAndRefreshChannel = async (type: ChannelType, newState: ChannelState) => {
    try {
      await notificationService.updateChannel(type, newState);
      await fetchChannels();
      showNotification({
        title: t('notifications:success-action'),
        type: 'success',
      });
    } catch (err) {
      showNotification({
        title: t('notifications:invalid-action'),
        type: 'error',
      });
    }
  };

  const toggleChannel = async (type: ChannelType, state: ChannelState) => {
    if (state === 'NOT_AVAILABLE') {
      if (type === 'SMS') {
        showVerifyPhonePopup(() => {
          updateAndRefreshChannel(type, 'ENABLED');
        });
      }
      return;
    }

    const newState: ChannelState = state === 'ENABLED' ? 'DISABLED' : 'ENABLED';
    await updateAndRefreshChannel(type, newState);
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  return {
    channels: response.content,
    loading: response.loading,
    refresh: fetchChannels,
    toggleChannel,
  };
};
